import { z } from 'zod/v4';

/**
 * Sort columns the admin users list endpoint accepts. Each key maps to a SQL
 * expression in `core/db/users/list-admin.ts` (`SORT_COLUMNS`) — keep the two
 * in sync when adding/removing columns.
 */
export const AdminUsersListSortColumns = [
  'firstName',
  'lastName',
  'role',
  'company',
  'primaryCompany',
  'createdAt',
  'updatedAt',
  'email',
  'isBlocked',
] as const;

export type AdminUsersListSortColumn =
  (typeof AdminUsersListSortColumns)[number];

const sortParamSchema = z
  .string()
  .regex(/^[a-zA-Z]+:(ASC|DESC|asc|desc)$/)
  .optional();

const booleanLike = z
  .union([z.boolean(), z.string()])
  .optional()
  .transform(v => v === true || v === 'true');

export const AdminUsersListQuerySchema = z.object({
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce.number().int().min(0).max(500).default(15),
  sort: sortParamSchema,
  q: z.string().max(255).optional(),
  filter: z.string().max(512).optional(),
  withDelegatedUsers: booleanLike,
});

export type AdminUsersListQuery = z.infer<typeof AdminUsersListQuerySchema>;

/**
 * Row shape returned by the admin users list. `looseObject` because the SQL
 * plus per-user enrichment returns ~20 fields and adding strict typing here
 * duplicates `backend/src/types/users` without buying validation strength for
 * an admin-only read endpoint.
 *
 * One row per user. `companyName` / `isPrimary` / `roleKeys` are only
 * populated when the request is scoped to one company via `filter.companyId`
 * — see `listAdminUsersDb` in `core/db/users/list-admin.ts`; otherwise they're
 * `null` and callers should read `primaryCompanyName` instead.
 * `isRetracedStaff` stays populated either way — it's independent of company
 * scope, and is the roster's signal for disabling "Impersonate" on a
 * Retraced-staff colleague.
 *
 * Field reads in the FE (must stay populated):
 *   id, firstName, lastName, avatarFileUrl, hasAgreedWithTerms,
 *   hasAgreedWithTermsAt, isRetracedStaff, primaryCompanyName,
 *   developmentTag, totalUserCompanies, email
 *
 * `roleKeys` (every IAM role key the user holds in the row's own company)
 * passes through the same way, following the same populated-only-when-
 * applicable rule. There is no display `role`/`roleName` column any more —
 * `role` still exists as a `filter`/`sort` query capability only (see
 * `FILTER_COLUMNS`/`SORT_COLUMNS` in `core/db/users/list-admin.ts`).
 */
export const AdminUserListRowSchema = z.looseObject({
  id: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string().nullable(),
  /** Does this user hold any role at all in the retraced staff tenant? */
  isRetracedStaff: z.boolean(),
  roleKeys: z.array(z.string()).nullable().optional(),
});

export type AdminUserListRow = z.infer<typeof AdminUserListRowSchema>;

export const AdminUsersListResponseSchema = z.object({
  data: z.array(AdminUserListRowSchema),
  meta: z.object({
    count: z.number(),
    page: z.number(),
    pageSize: z.number(),
    totalCount: z.number(),
  }),
});

export type AdminUsersListResponse = z.infer<
  typeof AdminUsersListResponseSchema
>;
